import React,{useEffect,useMemo,useState} from 'react';

export const EMPTY_ORGANIZATION_CONTEXT={
  organizationName:'',industry:'',organizationSize:'',geography:'',operatingModel:'',businessUnit:'',
  customerBase:'',regulatoryEnvironment:'',strategicPriorities:'',knownPainPoints:'',constraints:'',
  technologyLandscape:'',transformationContext:'',additionalContext:'',
};

const hasValue=value=>Object.values(value||{}).some(v=>String(v||'').trim());
const mergeBlank=(current,next)=>{
  const out={...EMPTY_ORGANIZATION_CONTEXT,...current};
  for(const [key,value] of Object.entries(next||{})){
    if(Object.prototype.hasOwnProperty.call(EMPTY_ORGANIZATION_CONTEXT,key)&&!String(out[key]||'').trim()&&String(value||'').trim()) out[key]=value;
  }
  return out;
};
const usefulSuggestionCount=(current,next)=>Object.keys(EMPTY_ORGANIZATION_CONTEXT).filter(key=>key!=='organizationName'&&!String(current?.[key]||'').trim()&&String(next?.[key]||'').trim()).length;

export default function OrganizationContextStep({
  value,onChange,repository,modelContext={},sourceSystem='',isExisting=false,onBack,onContinue,
}){
  const context={...EMPTY_ORGANIZATION_CONTEXT,...(value||{})};
  const [busy,setBusy]=useState('');
  const [message,setMessage]=useState('');
  const [messageType,setMessageType]=useState('info');
  const [saveAsDefault,setSaveAsDefault]=useState(false);
  const [defaultsLoaded,setDefaultsLoaded]=useState(false);

  useEffect(()=>{
    if(isExisting||hasValue(value)||defaultsLoaded)return;
    let alive=true;
    setBusy('defaults');
    repository.getModelContextDefaults().then(result=>{
      if(!alive)return;
      const defaults={...(result?.context||{}),organizationName:''};
      if(hasValue(defaults)){
        onChange({...EMPTY_ORGANIZATION_CONTEXT,...defaults});
        setMessageType('info');
        setMessage(result?.source==='workspace-profile'?'Started from your saved business context. Enter the organization/customer name for this model.':'Loaded your saved organization defaults. Enter the organization/customer name for this model.');
      }
    }).catch(()=>{}).finally(()=>{if(alive){setBusy('');setDefaultsLoaded(true);}});
    return()=>{alive=false};
  },[]);

  const set=(key)=>(e)=>{
    onChange({...context,[key]:e.target.value});
    if(key==='organizationName'){
      setMessage('');
      setMessageType('info');
    }
  };
  const aiReady=String(context.organizationName||'').trim().length>1;
  const processSummary=useMemo(()=>[modelContext.processType,modelContext.category].filter(Boolean).join(' · '),[modelContext]);

  const suggest=async()=>{
    if(!aiReady){setMessageType('error');setMessage('Enter the organization or customer name first.');return;}
    setBusy('ai');setMessageType('info');setMessage(`Using AI to build context for ${String(context.organizationName).trim()}…`);
    try{
      const result=await repository.suggestOrganizationContext({
        organizationName:context.organizationName,
        existingContext:context,
        model:{...modelContext,sourceSystem},
      });
      const suggested=result?.context||{};
      const count=usefulSuggestionCount(context,suggested);
      if(!count){
        throw new Error('AI did not return any usable organization details. Check the AI configuration and try again.');
      }
      onChange(mergeBlank(context,suggested));
      setMessageType('success');
      setMessage(`AI filled ${count} blank ${count===1?'field':'fields'}. Review the suggestions and add any internal context the AI cannot know.`);
    }catch(e){
      setMessageType('error');
      setMessage(e.message||'Unable to generate organization suggestions.');
    }finally{setBusy('');}
  };

  const continueNext=async()=>{
    setBusy('save');setMessage('');
    try{
      if(saveAsDefault) await repository.saveModelContextDefaults(context);
      await onContinue?.();
    }catch(e){setMessageType('error');setMessage(e.message||'Unable to save organization context.');}
    finally{setBusy('');}
  };

  return <section className="panel metricus-wizard-panel organization-context-panel">
    <div className="metricus-panel-head organization-context-head">
      <div>
        <h2>Organization context</h2>
        <p>Give Process Advisor the business context it needs to make recommendations that fit this organization, not just the process data.</p>
      </div>
    </div>

    <div className="metricus-panel-body organization-context-body">
      <div className="organization-context-intro">
        <div><b>Model-specific context</b><span>These values are stored with this model and included in AI advisory prompts.</span></div>
        {processSummary&&<span className="organization-process-chip">{processSummary}</span>}
      </div>

      <div className="organization-context-grid">
        <label className="span-two organization-name-field">
          <span>Organization / customer name</span>
          <div className="organization-name-ai-row">
            <input value={context.organizationName} onChange={set('organizationName')} placeholder="e.g. Acme Corporation"/>
            <button type="button" className="pa-primary organization-ai-button" disabled={!!busy||!aiReady} onClick={suggest}>
              {busy==='ai'?'Generating context…':'Auto-fill with AI'}
            </button>
          </div>
          <small className={`organization-ai-hint ${aiReady?'ready':''}`}>
            {aiReady
              ? <>AI is ready to suggest public and sector context for <b>{context.organizationName.trim()}</b>. It will only fill blank fields.</>
              : 'Enter the organization name first, then use AI to pre-fill the fields below.'}
          </small>
        </label>

        {message&&<div className={`organization-context-message ${messageType}`}>{message}</div>}
        {busy==='defaults'&&<div className="organization-context-message info">Loading your organization defaults…</div>}

        <label><span>Industry</span><input value={context.industry} onChange={set('industry')} placeholder="e.g. Higher education"/></label>
        <label><span>Organization size</span><input value={context.organizationSize} onChange={set('organizationSize')} placeholder="e.g. Large enterprise / 10,000+ employees"/></label>
        <label><span>Primary geography</span><input value={context.geography} onChange={set('geography')} placeholder="e.g. United States"/></label>
        <label><span>Operating model</span><input value={context.operatingModel} onChange={set('operatingModel')} placeholder="e.g. Central IT with federated business teams"/></label>
        <label><span>Business unit / function</span><input value={context.businessUnit} onChange={set('businessUnit')} placeholder="e.g. Technology Operations"/></label>
        <label><span>Customers / users served</span><input value={context.customerBase} onChange={set('customerBase')} placeholder="Internal employees, students, consumers, enterprise customers…"/></label>
        <label className="span-two"><span>Regulatory / compliance environment</span><textarea rows={2} value={context.regulatoryEnvironment} onChange={set('regulatoryEnvironment')} placeholder="Relevant obligations, audit requirements, data residency, safety or service commitments"/></label>
        <label className="span-two"><span>Strategic priorities</span><textarea rows={2} value={context.strategicPriorities} onChange={set('strategicPriorities')} placeholder="What matters most right now: cost, growth, service quality, automation, risk reduction…"/></label>
        <label className="span-two"><span>Known pain points</span><textarea rows={2} value={context.knownPainPoints} onChange={set('knownPainPoints')} placeholder="Known organizational or operational problems the advisor should keep in mind"/></label>
        <label className="span-two"><span>Constraints</span><textarea rows={2} value={context.constraints} onChange={set('constraints')} placeholder="Budget, headcount, skills, vendor, policy, timing or change constraints"/></label>
        <label className="span-two"><span>Technology landscape</span><textarea rows={2} value={context.technologyLandscape} onChange={set('technologyLandscape')} placeholder="Core platforms, integrations, automation tools and major technology dependencies"/></label>
        <label className="span-two"><span>Transformation / change context</span><textarea rows={2} value={context.transformationContext} onChange={set('transformationContext')} placeholder="Current programs, migrations, restructures or transformation initiatives"/></label>
        <label className="span-two"><span>Additional context</span><textarea rows={2} value={context.additionalContext} onChange={set('additionalContext')} placeholder="Anything else that should influence the AI recommendations"/></label>
      </div>

      <div className="organization-default-row">
        <label><input type="checkbox" checked={saveAsDefault} onChange={e=>setSaveAsDefault(e.target.checked)}/><span>Use these values as my defaults for future models in this workspace</span></label>
        <small>Future models start with a copy of these defaults. Each model keeps its own editable values.</small>
      </div>
    </div>

    <div className="metricus-panel-footer">
      <button className="pa-secondary" disabled={!!busy} onClick={onBack}>Back</button>
      <button className="pa-primary" disabled={!!busy} onClick={continueNext}>{busy==='save'?'Saving…':'Continue'}</button>
    </div>
  </section>;
}
